import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Clock, Medal } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SLOT_LABEL = { mattina: "Mattina", pomeriggio: "Pomeriggio", notte: "Notte", intermedio: "Intermedio", spezzato: "Spezzato" };
const SLOT_ORDER = ["mattina", "pomeriggio", "notte", "intermedio", "spezzato"];
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtLong = (iso) => { try { return capFirst(new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })); } catch { return iso; } };

export function TurniGiornoBox({ giorni = [], title = "Chi era in turno", compact = false }) {
  const { selectedStructure } = useAuth();
  const navigate = useNavigate();
  const days = (Array.isArray(giorni) ? giorni : [giorni]).filter(Boolean);
  const { data: turni = [] } = useQuery({
    enabled: days.length > 0,
    queryKey: ["turni-giorno-box", selectedStructure],
    queryFn: async () => (await api.get("/turni", { params: { structure_id: selectedStructure } })).data,
  });
  if (!days.length) return null;
  const multi = days.length > 1;

  const badge = (t, d, i) => {
    const clickable = !!t.operatore_id;
    const Tag = clickable ? "button" : "span";
    return (
      <Tag key={i} {...(clickable ? { onClick: () => navigate(`/operatori/${t.operatore_id}`), type: "button" } : {})}
        data-testid={clickable ? `turni-op-${d}-${t.operatore_id}` : undefined}
        className={`text-xs font-semibold rounded-full px-2.5 py-1 ${t.leader ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-sky-50 text-sky-800 border border-sky-200"} ${clickable ? "hover:bg-sky-100 hover:underline cursor-pointer transition-colors" : ""}`}>
        {t.leader && <Medal size={11} className="inline mr-1" style={{ color: "#b45309" }} />}{t.operatore_nome}{t.qualifica ? <span className="font-normal opacity-70"> · {t.qualifica}</span> : ""}
      </Tag>
    );
  };

  if (compact && !multi) {
    const d = days[0];
    const list = (turni || []).filter((t) => t.status !== "eliminato" && t.data === d);
    const MAP = { mat_pom: ["mattina", "pomeriggio"], not_mat: ["notte", "mattina"], sera: ["notte"] };
    const bySlot = { mattina: [], pomeriggio: [], notte: [] };
    const cucina = [];
    list.forEach((t) => {
      if (t.turno === "cucina" || (t.qualifica || "").toUpperCase() === "CUCINA") { cucina.push(t); return; }
      (MAP[t.turno] || [t.turno]).forEach((k) => { (bySlot[k] = bySlot[k] || []).push(t); });
    });
    const cols = ["mattina", "pomeriggio", "notte", ...Object.keys(bySlot).filter((k) => !["mattina", "pomeriggio", "notte"].includes(k))];
    const chip = (t, i) => (
      <button key={i} type="button" onClick={t.operatore_id ? () => navigate(`/operatori/${t.operatore_id}`) : undefined}
        data-testid={t.operatore_id ? `turni-op-${d}-${t.operatore_id}` : undefined}
        className={`text-[11px] font-semibold leading-tight rounded px-1.5 py-0.5 hover:underline ${t.leader ? "bg-amber-100 text-amber-800" : "bg-sky-50 text-sky-900"}`}>
        {t.leader && <Medal size={10} className="inline mr-0.5" style={{ color: "#b45309" }} />}{t.operatore_nome}{t.qualifica && (t.qualifica || "").toUpperCase() !== "CUCINA" ? <span className="font-normal opacity-60"> {t.qualifica}</span> : ""}{t.orario ? <span className="font-normal opacity-60"> {t.orario}</span> : ""}
      </button>
    );
    return (
      <div className="gr-card px-3 py-2" data-testid="turni-giorno-box">
        <h3 className="font-head font-bold text-xs flex items-center gap-1.5 mb-1.5"><Clock size={14} className="text-sky-600" /> {title}</h3>
        {list.length === 0 ? (
          <span className="text-xs text-muted-foreground" data-testid={`turni-giorno-empty-${d}`}>Nessun turno registrato in questo giorno.</span>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-left border-collapse" style={{ minWidth: 0 }} data-testid="turni-giorno-table">
              <thead>
                <tr>{cols.map((k) => <th key={k} className="text-[10px] font-bold uppercase tracking-wide text-sky-700 bg-sky-50/70 border border-sky-100 px-2 py-1" style={{ width: `${100 / cols.length}%` }}>{SLOT_LABEL[k] || k}</th>)}</tr>
              </thead>
              <tbody>
                <tr>{cols.map((k) => (
                  <td key={k} className="border border-sky-100 px-1.5 py-1 align-top" data-testid={`turni-giorno-slot-${d}-${k}`}>
                    {bySlot[k].length ? <div className="flex flex-wrap gap-1">{bySlot[k].map(chip)}</div> : <span className="text-[11px] text-muted-foreground">—</span>}
                  </td>
                ))}</tr>
                {cucina.length > 0 && (
                  <tr data-testid={`turni-giorno-cucina-${d}`}>
                    <td colSpan={cols.length} className="border border-orange-100 bg-orange-50/60 px-1.5 py-0.5">
                      <div className="flex flex-wrap items-center gap-1">
                        <span className="text-[10px] font-bold uppercase tracking-wide text-orange-700 mr-1">Cucina</span>
                        {cucina.map(chip)}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="gr-card p-4" data-testid="turni-giorno-box">
      <h3 className="font-head font-bold text-base mb-3 flex items-center gap-2"><Clock size={17} className="text-sky-600" /> {title}</h3>
      <div className="space-y-4">
        {days.map((d) => {
          const list = (turni || []).filter((t) => t.status !== "eliminato" && t.data === d);
          const bySlot = {};
          list.forEach((t) => { (bySlot[t.turno] = bySlot[t.turno] || []).push(t); });
          const slotKeys = [...new Set([...SLOT_ORDER.filter((s) => bySlot[s]), ...Object.keys(bySlot)])];
          return (
            <div key={d} data-testid={`turni-giorno-${d}`}>
              {multi && <div className="text-sm font-semibold capitalize mb-2 text-sky-800">{fmtLong(d)}</div>}
              {list.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid={`turni-giorno-empty-${d}`}>Nessun turno registrato{multi ? "" : " in questo giorno"}.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {slotKeys.map((k) => (
                    <div key={k} className="rounded-xl border bg-white p-3" data-testid={`turni-giorno-slot-${d}-${k}`}>
                      <div className="text-xs font-bold uppercase tracking-wide text-sky-700 mb-2">{SLOT_LABEL[k] || k}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {bySlot[k].map((t, i) => badge(t, d, i))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
